import {addDays, eachDayOfInterval} from 'date-fns';
import {
  DAY_NAMES,
  Offer,
  OFFER_CATEGORIES,
  OFFER_STATUSES,
  getFeatureFlags,
} from '@chekin/core';
import type {
  BookingSummary,
  OfferDetails,
  RadicalStorageBooking,
  RequestedDealItem,
} from '@chekin/core';
import type {SelectOption} from '@chekinapp/ui';
import {
  describeDealSelection,
  indexDealsById,
  isLuggageServiceOffer,
  isTimeBasedOffer,
  parseTimeFromName,
  resolveDealTitle,
} from '@chekin/shared/payments';
import luggageStorageImage from 'assets/img/luggage-storage.webp';

export function isLuggageStorageEnabled(): boolean {
  return Boolean(getFeatureFlags('CHE_184_LUGGAGE_STORAGE_ENABLED'));
}

type GuruWalkSummary = Partial<Pick<BookingSummary, 'guruwalk_status' | 'location'>>;

export function isGuruWalkAvailable(summary?: GuruWalkSummary | null): boolean {
  return summary?.guruwalk_status === OFFER_STATUSES.active && Boolean(summary?.location);
}

type OfferCategoryInput = Partial<Pick<Offer, 'category'>> | null | undefined;

const DELIVERY_EXCLUDED_CATEGORIES = new Set<string>([
  OFFER_CATEGORIES.petFee,
  OFFER_CATEGORIES.petFriendly,
  OFFER_CATEGORIES.propertyProtection,
  OFFER_CATEGORIES.luggageServices,
]);

export function requiresDeliveryDateTime(
  category: OFFER_CATEGORIES | undefined,
  requiresDelivery: boolean = true,
): boolean {
  return (
    requiresDelivery &&
    !isTimeBasedOffer({category}) &&
    !DELIVERY_EXCLUDED_CATEGORIES.has(category ?? '')
  );
}

const EMAIL_REQUIRED_CATEGORIES = new Set<string>([OFFER_CATEGORIES.luggageServices]);

export function requiresGuestEmail(category?: OFFER_CATEGORIES | string): boolean {
  return EMAIL_REQUIRED_CATEGORIES.has(category ?? '');
}

export function toISODate(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

export const toTimeOption = (value?: string | null): SelectOption | null =>
  value ? {value, label: value} : null;

export function getLuggageOfferImage(
  category?: OFFER_CATEGORIES | string,
): string | undefined {
  return category === OFFER_CATEGORIES.luggageServices ? luggageStorageImage : undefined;
}

export type BookingLifecycle = 'active' | 'completed';

type BookingLifecycleInput = Pick<RadicalStorageBooking, 'status' | 'pick_up'>;

export function getBookingLifecycle(booking: BookingLifecycleInput): BookingLifecycle {
  if (booking.status === 'COMPLETED') return 'completed';
  if (booking.pick_up && new Date(booking.pick_up).getTime() < Date.now())
    return 'completed';
  return 'active';
}

export const isActiveBooking = (booking: BookingLifecycleInput): boolean =>
  getBookingLifecycle(booking) === 'active';

export function buildRequestedForOffer(
  offer: Offer,
  itemQuantities: Record<string, number>,
  checkInDate: string | null | undefined,
  checkOutDate: string | null | undefined,
): string | null {
  const selectedItem = offer.items.find(item => itemQuantities[item.id] > 0);
  const time = selectedItem?.name ? parseTimeFromName(selectedItem.name) : null;
  if (!time) return null;

  if (offer.category === OFFER_CATEGORIES.checkIn && checkInDate) {
    return `${checkInDate}T${time}:00`;
  }
  if (offer.category === OFFER_CATEGORIES.checkOut && checkOutDate) {
    return `${checkOutDate}T${time}:00`;
  }
  return null;
}

export const buildRequestedItemsForOffer = (
  offer: OfferCategoryInput,
  itemQuantities: Record<string, number>,
): {requestedItems: RequestedDealItem[]; numberOfPeople: number} => {
  const requestedItems = Object.entries(itemQuantities)
    .filter(([, qty]) => qty > 0)
    .map(([itemId, qty]) => ({
      item: itemId,
      quantity: qty,
    }));

  if (!isTimeBasedOffer(offer)) {
    return {
      requestedItems,
      numberOfPeople: requestedItems.reduce((sum, {quantity}) => sum + quantity, 0),
    };
  }

  const selectedItem = requestedItems[0];

  return {
    requestedItems: selectedItem ? [{item: selectedItem.item, quantity: 1}] : [],
    numberOfPeople: selectedItem ? 1 : 0,
  };
};

function getAvailableDates(
  effectiveMin: string,
  checkOutDate: string | null | undefined,
): Date[] {
  const start = new Date(`${effectiveMin}T00:00:00`);
  const hardEnd = addDays(start, 13);
  const end = checkOutDate
    ? new Date(
        Math.min(new Date(`${checkOutDate}T00:00:00`).getTime(), hardEnd.getTime()),
      )
    : hardEnd;
  if (end < start) return [];
  return eachDayOfInterval({start, end});
}

function getBookableDates(
  checkInDate?: string | null,
  checkOutDate?: string | null,
): Date[] {
  const today = toISODate(new Date());
  const effectiveMin = checkInDate && checkInDate > today ? checkInDate : today;

  return getAvailableDates(effectiveMin, checkOutDate);
}

function hasBookableDates(
  checkInDate?: string | null,
  checkOutDate?: string | null,
): boolean {
  return getBookableDates(checkInDate, checkOutDate).length > 0;
}

function describeBookableRange(
  locale: string,
  checkInDate?: string | null,
  checkOutDate?: string | null,
): string | null {
  const dates = getBookableDates(checkInDate, checkOutDate);
  if (!dates.length) return null;

  const formatter = new Intl.DateTimeFormat(locale, {day: 'numeric', month: 'short'});
  const first = dates[0];
  const last = dates[dates.length - 1];

  return first.getTime() === last.getTime()
    ? formatter.format(first)
    : formatter.formatRange(first, last);
}

type DurationAvailability = NonNullable<
  OfferDetails['availability']
>['duration_availabilities'][number];

const WEEKDAY_ORDER: DAY_NAMES[] = [
  DAY_NAMES.monday,
  DAY_NAMES.tuesday,
  DAY_NAMES.wednesday,
  DAY_NAMES.thursday,
  DAY_NAMES.friday,
  DAY_NAMES.saturday,
  DAY_NAMES.sunday,
];

function weekdayLabel(index: number, locale: string): string {
  const reference = new Date(Date.UTC(2024, 0, 1 + index));
  return new Intl.DateTimeFormat(locale, {weekday: 'short', timeZone: 'UTC'}).format(
    reference,
  );
}

const toHourLabel = (value?: string) => (value ? value.slice(0, 5) : '');

function describeAvailabilityWindow(
  availabilities: DurationAvailability[] | undefined,
  locale: string,
): {days: string; hours: string | null; isEveryDay: boolean} | null {
  const open = (availabilities ?? []).filter(a => a.weekday);
  if (!open.length) return null;

  const openDays = WEEKDAY_ORDER.map((day, index) => ({day, index})).filter(({day}) =>
    open.some(a => a.weekday === day),
  );
  if (!openDays.length) return null;

  const isEveryDay = openDays.length === WEEKDAY_ORDER.length;
  const days = openDays.map(({index}) => weekdayLabel(index, locale)).join(', ');

  const timed = open.filter(a => !a.is_all_day && a.hours_start && a.hours_end);
  const sharesOneWindow =
    timed.length === open.length &&
    timed.every(
      a => a.hours_start === timed[0].hours_start && a.hours_end === timed[0].hours_end,
    );
  const hours =
    timed.length && sharesOneWindow
      ? `${toHourLabel(timed[0].hours_start)}–${toHourLabel(timed[0].hours_end)}`
      : null;

  return {days, hours, isEveryDay};
}

export {
  getBookableDates,
  hasBookableDates,
  describeAvailabilityWindow,
  describeBookableRange,
};

export {
  describeDealSelection,
  indexDealsById,
  isLuggageServiceOffer,
  isTimeBasedOffer,
  resolveDealTitle,
};
